import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ActingAsBanner } from '../../pages/Supervisor';
import { moduleColor } from '../../utils/modules';
import Topbar from './Topbar';
import Sidebar from './Sidebar';

export default function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  // A page can ask for the full width (e.g. the compact order screen).
  const [wide, setWide] = useState(false);
  // Below `lg` the rail has no room to sit beside the page, so it becomes an
  // off-canvas drawer with its own open state. The desktop `collapsed` flag is
  // left alone: switching back to a wide window restores whatever it was.
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Pages reach these through useOutletContext().
  const shell = useMemo(() => ({ setCollapsed, setWide }), []);
  const location = useLocation();
  // The current menu's colour, picked up by headers, tables and the sidebar.
  const mod = moduleColor(location.pathname);

  // A tap on a menu item navigates and the drawer has done its job.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // The drawer sits over the page, so the page behind it must not scroll.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [drawerOpen]);

  return (
    // `h-dvh` rather than `h-screen`: on a phone the browser's own chrome eats
    // into the viewport, and `100vh` would push the bottom of the app under it.
    <div className="print-reset flex h-dvh flex-col overflow-hidden">
      <ActingAsBanner />
      <Topbar
        onToggleSidebar={() => setCollapsed((v) => !v)}
        onOpenDrawer={() => setDrawerOpen(true)}
        collapsed={collapsed}
      />
      <div className="print-reset flex flex-1 overflow-hidden">
        {/* Desktop rail — in the flow, pushing the page across. */}
        <aside className={`no-print hidden shrink-0 overflow-hidden transition-[width] duration-200 ease-out lg:block ${collapsed ? 'w-[3.75rem]' : 'w-[13rem]'}`}>
          <Sidebar collapsed={collapsed} onNavigate={() => setCollapsed(false)} />
        </aside>

        {/* Mobile drawer — over the page, dismissed by the scrim or Escape. */}
        {drawerOpen && (
          <div
            className="no-print fixed inset-0 z-40 bg-content/40 backdrop-blur-[1px] animate-fade-in lg:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
        )}
        <aside
          className={`no-print fixed inset-y-0 left-0 z-50 w-[15rem] max-w-[82vw] shadow-pop transition-transform duration-200 ease-out lg:hidden ${
            drawerOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          aria-hidden={!drawerOpen}
        >
          <Sidebar collapsed={false} onNavigate={() => setDrawerOpen(false)} onClose={() => setDrawerOpen(false)} />
        </aside>

        <main
          className="print-reset min-w-0 flex-1 overflow-y-auto"
          data-module={mod ? '' : undefined}
          style={mod ? { '--mod': mod } : undefined}
        >
          {/* The page fills the window rather than sitting in a fixed column:
              the room the sidebar gives up belongs to the listing, not to the
              margin. The cap only stops a table stretching across a very wide
              monitor. */}
          <div className={`print-reset mx-auto py-4 sm:py-6 ${
            wide || collapsed ? 'max-w-none px-3 lg:px-4' : 'max-w-[120rem] px-3 sm:px-5 lg:px-7'
          }`}>
            <Outlet context={shell} />
          </div>
        </main>
      </div>
    </div>
  );
}
